function isBackoffSet(set) {
  return (
    set &&
    set.backoffPercent !== undefined &&
    set.backoffPercent !== null &&
    set.backoffPercent !== "" &&
    set.backoffFromIndex !== undefined &&
    set.backoffFromIndex !== null &&
    set.backoffFromIndex !== ""
  );
}

function normalizeLoggedWeight(weight) {
  if (weight === undefined || weight === null || weight === "") return null;
  const n = Number(weight);
  if (!Number.isNaN(n)) return n;
  const trimmed = String(weight).trim();
  return trimmed || null;
}

function normalizeLoggedReps(reps) {
  if (reps === undefined || reps === null || String(reps).trim() === "") {
    return null;
  }
  if (typeof reps === "number" && Number.isFinite(reps)) return reps;
  const trimmed = String(reps).trim();
  if (/^-?\d+$/.test(trimmed)) return parseInt(trimmed, 10);
  return trimmed;
}

function getMajorityValue(values) {
  const counts = new Map();
  for (const value of values) {
    const key = String(value);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let best = null;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      best = value;
    }
  }
  return best;
}

/**
 * Flags sets whose logged weight/reps differ from other sets in the same exercise
 * (majority outlier or changed from the previous set — client may have adjusted numbers).
 */
export function getLoggedSetVarianceFlags(sets = []) {
  const flags = sets.map(() => ({ repsDiffers: false, weightDiffers: false }));
  if (!Array.isArray(sets) || sets.length < 2) return flags;

  const comparable = sets
    .map((set, index) => ({ set, index }))
    .filter(({ set }) => !isBackoffSet(set));

  if (comparable.length < 2) return flags;

  const weights = comparable
    .map(({ set }) => normalizeLoggedWeight(set.weight))
    .filter((value) => value !== null);
  const weightVaries = new Set(weights.map(String)).size > 1;

  const reps = comparable
    .map(({ set }) => normalizeLoggedReps(set.reps))
    .filter((value) => value !== null);
  const repsVaries = new Set(reps.map(String)).size > 1;

  const majorityWeight = weightVaries
    ? getMajorityValue(weights.map(String))
    : null;
  const majorityReps = repsVaries ? getMajorityValue(reps.map(String)) : null;

  for (const { set, index } of comparable) {
    const weight = normalizeLoggedWeight(set.weight);
    const rep = normalizeLoggedReps(set.reps);

    if (weightVaries && weight !== null && String(weight) !== majorityWeight) {
      flags[index].weightDiffers = true;
    }
    if (repsVaries && rep !== null && String(rep) !== majorityReps) {
      flags[index].repsDiffers = true;
    }
  }

  for (let i = 1; i < sets.length; i++) {
    if (isBackoffSet(sets[i])) continue;

    const prevWeight = normalizeLoggedWeight(sets[i - 1]?.weight);
    const currWeight = normalizeLoggedWeight(sets[i]?.weight);
    if (
      prevWeight !== null &&
      currWeight !== null &&
      String(currWeight) !== String(prevWeight)
    ) {
      flags[i].weightDiffers = true;
    }

    const prevReps = normalizeLoggedReps(sets[i - 1]?.reps);
    const currReps = normalizeLoggedReps(sets[i]?.reps);
    if (
      prevReps !== null &&
      currReps !== null &&
      String(currReps) !== String(prevReps)
    ) {
      flags[i].repsDiffers = true;
    }
  }

  return flags;
}

export const setVarianceHighlightSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 167, 38, 0.16)"
        : theme.palette.warning.light,
  },
});
